import React, { useEffect, useState, useCallback } from 'react';
import { registerConflictHandler, subscribeQueueSize, subscribeDrainEvents } from '../../utils/offlineWriteQueue';

/**
 * OfflineSyncManager — invisible glue component that:
 *   1. Subscribes to the offline write queue's conflict events and surfaces
 *      a modal so the user can resolve them.
 *   2. Displays a transient toast-style summary while the queue drains.
 *
 * Mounted once at the App root, no props.
 */

const friendlyEntity = (kind) => {
  if (kind === 'task') return 'task';
  if (kind === 'contact') return 'contact';
  if (kind === 'category') return 'category';
  return 'item';
};

const describeOp = (op) => {
  const verb = op.method === 'POST' ? 'created' : op.method === 'PUT' ? 'edited' : 'deleted';
  return `${friendlyEntity(op.entity_kind)} you ${verb} offline`;
};

const OfflineSyncManager = () => {
  const [pendingCount, setPendingCount] = useState(0);
  const [draining, setDraining] = useState(false);
  const [conflict, setConflict] = useState(null); // { op, serverResponse, resolve }
  const [syncToast, setSyncToast] = useState(null);

  // Conflict handler — returns a Promise the queue can await for the user's
  // decision. Resolutions: 'keep_mine' | 'keep_theirs' | 'skip'.
  useEffect(() => {
    const handler = ({ op, serverResponse }) =>
      new Promise((resolve) => {
        setConflict({ op, serverResponse, resolve });
      });
    registerConflictHandler(handler);
    return () => registerConflictHandler(null);
  }, []);

  // Pending-count + drain-state pub-sub.
  useEffect(() => {
    const unsubSize = subscribeQueueSize((count) => setPendingCount(count));
    const unsubEvents = subscribeDrainEvents((event) => {
      if (event.type === 'drain_started') {
        setDraining(true);
      } else if (event.type === 'drain_finished') {
        setDraining(false);
        // Show a brief success toast if the queue actually shrank.
        setSyncToast({ kind: 'done', text: 'All changes synced' });
        window.setTimeout(() => setSyncToast(null), 2500);
      } else if (event.type === 'op_failed') {
        setSyncToast({
          kind: 'error',
          text: `Couldn't save ${friendlyEntity(event.op.entity_kind)} — please try again.`,
        });
        window.setTimeout(() => setSyncToast(null), 4000);
      }
    });
    return () => {
      unsubSize();
      unsubEvents();
    };
  }, []);

  const resolveConflict = useCallback((resolution) => {
    if (!conflict) return;
    try {
      conflict.resolve(resolution);
    } finally {
      setConflict(null);
    }
  }, [conflict]);

  return (
    <>
      {/* Pending changes pill — only renders when there is something queued. */}
      {pendingCount > 0 && (
        <div
          data-testid="offline-pending-pill"
          className="fixed bottom-4 left-4 z-[99] bg-indigo-600 text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5"
        >
          {draining && (
            <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.3" strokeWidth="4" />
              <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
            </svg>
          )}
          {draining ? 'Syncing…' : `${pendingCount} pending change${pendingCount === 1 ? '' : 's'}`}
        </div>
      )}

      {/* Transient sync result toast. */}
      {syncToast && !conflict && (
        <div
          data-testid="offline-sync-toast"
          className={`fixed bottom-16 left-1/2 -translate-x-1/2 z-[99] px-4 py-2 rounded-xl shadow-lg text-sm font-medium ${
            syncToast.kind === 'error' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'
          }`}
        >
          {syncToast.text}
        </div>
      )}

      {/* Conflict modal — only renders when the queue surfaces a 409. */}
      {conflict && (
        <div
          data-testid="offline-conflict-modal"
          className="fixed inset-0 z-[101] flex items-center justify-center bg-black/50 p-4"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-amber-600 text-xl">⚠️</span>
              <h3 className="text-lg font-semibold text-gray-900">Sync conflict</h3>
            </div>
            <p className="text-sm text-gray-700 mb-4">
              Someone else changed this {describeOp(conflict.op)} on another device
              while you were offline. Which version do you want to keep?
            </p>

            {/* Side-by-side preview — best-effort */}
            <div className="grid grid-cols-1 gap-3 mb-4">
              <div className="border border-gray-200 rounded-xl p-3 bg-gray-50">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Your version</p>
                <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words max-h-32 overflow-y-auto font-mono">
{JSON.stringify(conflict.op.body || {}, null, 2)}
                </pre>
              </div>
              <div className="border border-gray-200 rounded-xl p-3 bg-gray-50">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Server version</p>
                <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words max-h-32 overflow-y-auto font-mono">
{JSON.stringify(conflict.serverResponse || {}, null, 2)}
                </pre>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <button
                onClick={() => resolveConflict('keep_mine')}
                data-testid="conflict-keep-mine"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl font-medium"
              >
                Keep mine (overwrite server)
              </button>
              <button
                onClick={() => resolveConflict('keep_theirs')}
                data-testid="conflict-keep-theirs"
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-900 py-2.5 rounded-xl font-medium"
              >
                Keep server's version
              </button>
              <button
                onClick={() => resolveConflict('skip')}
                data-testid="conflict-skip"
                className="w-full text-gray-500 hover:text-gray-700 text-sm py-1.5"
              >
                Decide later
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OfflineSyncManager;
